import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Input,
  IconButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Image,
  Text,
  Flex,
  Heading,
  useToast,
  HStack,
} from "@chakra-ui/react";
import { SearchIcon, AddIcon, DeleteIcon } from "@chakra-ui/icons";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext";
import AlbumModal from "../modals/AlbumModal";
import { useNavigate } from "react-router-dom";

const AlbumTab = () => {
  const { user } = useAuth();
  const [draftAlbums, setDraftAlbums] = useState([]);
  const [ownedAlbums, setOwnedAlbums] = useState([]);
  const [totalOwnedAlbums, setTotalOwnedAlbums] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentSearchTerm, setCurrentSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [tempPage, setTempPage] = useState("1");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const itemsPerPage = 15;
  const navigate = useNavigate();
  const toast = useToast();

  // 작성 중인 앨범 데이터 가져오기
  const fetchDraftAlbums = async (searchKeyword = "") => {
    try {
      const { data, error } = await supabase.rpc("fetch_album_data_by_user", {
        target_email: user.email,
        type: "draft",
        search_keyword: searchKeyword,
        page_limit: itemsPerPage,
        page_offset: 0,
      });

      if (error) throw error;

      if (data && data.length > 0) {
        const albums = data.map(({ total_count, ...rest }) => rest);
        setDraftAlbums(albums);
      } else {
        setDraftAlbums([]);
      }
    } catch (error) {
      console.error("Error fetching draft albums:", error);
      toast({
        title: "앨범 데이터 로딩 실패",
        description: "작성 중인 앨범 데이터를 가져오는데 실패했습니다.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // 소유한 앨범 데이터 가져오기
  const fetchOwnedAlbums = async (searchKeyword = "") => {
    try {
      const { data, error } = await supabase.rpc("get_album_tracks_by_owner", {
        target_email: user.email,
        type: "owned",
        search_keyword: searchKeyword,
        page_limit: itemsPerPage,
        page_offset: (currentPage - 1) * itemsPerPage,
      });

      if (error) throw error;

      if (data && data.length > 0) {
        const totalCount = data[0].total_count;
        const albums = data.map(({ total_count, ...rest }) => rest);
        setTotalOwnedAlbums(totalCount);
        setOwnedAlbums(albums);
      } else {
        setTotalOwnedAlbums(0);
        setOwnedAlbums([]);
      }
    } catch (error) {
      console.error("Error fetching owned albums:", error);
      toast({
        title: "앨범 데이터 로딩 실패",
        description: "소유한 앨범 데이터를 가져오는데 실패했습니다.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // 검색 실행 함수
  const handleSearch = () => {
    setCurrentSearchTerm(searchTerm);
    setCurrentPage(1);
    setTempPage("1");
    fetchDraftAlbums(searchTerm);
    fetchOwnedAlbums(searchTerm);
  };

  // 엔터 키 처리
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // 페이지 이동 핸들러
  const handlePageMove = () => {
    if (/^\d+$/.test(tempPage)) {
      const page = parseInt(tempPage);
      const maxPage = Math.ceil(totalOwnedAlbums / itemsPerPage);
      if (page >= 1 && page <= maxPage) {
        setCurrentPage(page);
      } else {
        setTempPage(currentPage.toString());
      }
    } else {
      setTempPage(currentPage.toString());
    }
  };

  // 페이지 입력 핸들러
  const handlePageInput = (e) => {
    const value = e.target.value;
    setTempPage(value);
  };

  // currentPage가 바뀔 때마다 tempPage 동기화
  useEffect(() => {
    setTempPage(currentPage.toString());
  }, [currentPage]);

  // 검색어 변경 시 페이지 초기화
  useEffect(() => {
    setCurrentPage(1);
    setTempPage("1");
  }, [currentSearchTerm]);

  // 초기 데이터 로딩
  useEffect(() => {
    if (user) {
      fetchDraftAlbums(currentSearchTerm);
      fetchOwnedAlbums(currentSearchTerm);
    }
  }, [user, currentPage]);

  const handleDeleteAlbum = async (id, e) => {
    e.stopPropagation(); // 이벤트 버블링 방지
    try {
      // 1. 먼저 해당 앨범의 모든 트랙 삭제
      const { error: trackError } = await supabase
        .from("track")
        .delete()
        .eq("album", id);

      if (trackError) throw trackError;

      // 2. 앨범 삭제
      const { error: albumError } = await supabase
        .from("album")
        .delete()
        .eq("id", id);

      if (albumError) throw albumError;

      toast({
        title: "앨범과 관련 트랙이 삭제되었습니다.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      fetchDraftAlbums(currentSearchTerm);
    } catch (error) {
      toast({
        title: "앨범 삭제 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // 앨범 테이블 렌더링 함수
  const renderAlbumTable = (
    data,
    showPagination = false,
    title = "",
    isDraft = false
  ) => {
    if (!data || data.length === 0) return null;

    // 페이지네이션 정보 계산
    const totalPages = showPagination
      ? Math.ceil(totalOwnedAlbums / itemsPerPage)
      : 1;

    return (
      <Box mb={8}>
        {title && (
          <Heading size="md" mb={4}>
            {title}
          </Heading>
        )}
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>앨범명</Th>
              {!isDraft && <Th>ISRC</Th>}
              <Th>뮤지션</Th>
              <Th display={{ base: "none", md: "table-cell" }}>레이블</Th>
              <Th display={{ base: "none", md: "table-cell" }}>등록일</Th>
              <Th display={{ base: "none", md: "table-cell" }}>
                {isDraft ? "상태" : "발매일"}
              </Th>
              {isDraft && <Th>관리(앨범 전체 삭제)</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {data.map((item, index) => (
              <Tr
                key={item.album_id + index}
                onClick={() => navigate(`/album/${item.album_id}`)}
                cursor="pointer"
                _hover={{ bg: isDraft ? "gray.100" : "gray.50" }}
                bg={isDraft ? "gray.50" : "white"}
              >
                <Td>
                  <Flex alignItems="center">
                    <Image
                      src={item.cover || item.cover_url}
                      alt={item.name_album}
                      boxSize="40px"
                      objectFit="cover"
                      mr={3}
                      fallbackSrc="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40'><rect width='100%' height='100%' fill='white'/></svg>"
                    />
                    <Box>
                      <Text>{item.name_track || item.name_album}</Text>
                      {item.name_track && (
                        <Text fontSize="sm" color="gray.500">
                          {item.name_album}
                        </Text>
                      )}
                    </Box>
                  </Flex>
                </Td>
                {!isDraft && (
                  <Td>
                    {item.isrc && item.isrc !== "해외 발매 X" ? item.isrc : "-"}
                  </Td>
                )}
                <Td>
                  {item.musician && (
                    <Flex alignItems="center">
                      <Image
                        src={item.musician.image_profile}
                        alt={item.musician.name_musician}
                        boxSize="30px"
                        objectFit="cover"
                        borderRadius="full"
                        mr={2}
                        fallbackSrc="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='30' height='30'><rect width='100%' height='100%' fill='white'/></svg>"
                      />
                      <Text>{item.musician.name_musician}</Text>
                    </Flex>
                  )}
                </Td>
                <Td display={{ base: "none", md: "table-cell" }}>
                  {item.label && (
                    <Flex alignItems="center">
                      <Image
                        src={item.label.logo}
                        alt={item.label.name}
                        boxSize="30px"
                        objectFit="cover"
                        borderRadius="full"
                        mr={2}
                        fallbackSrc="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='30' height='30'><rect width='100%' height='100%' fill='white'/></svg>"
                      />
                      <Text>{item.label.name}</Text>
                    </Flex>
                  )}
                </Td>
                <Td display={{ base: "none", md: "table-cell" }}>
                  {item.registered_at
                    ? new Date(item.registered_at).toLocaleDateString("ko-KR")
                    : "-"}
                </Td>
                <Td display={{ base: "none", md: "table-cell" }}>
                  {isDraft ? (
                    <Text color="blue.500" fontWeight="bold">
                      작업중
                    </Text>
                  ) : item.status_musician === "[관리자] 발매 완료" ? (
                    item.published_at ? (
                      new Date(item.published_at).toLocaleDateString("ko-KR")
                    ) : (
                      "발매 완료"
                    )
                  ) : (
                    "발매 대기중(최대 2주 소요)"
                  )}
                </Td>
                {isDraft && (
                  <Td>
                    <IconButton
                      icon={<DeleteIcon />}
                      colorScheme="red"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleDeleteAlbum(item.album_id, e)}
                      aria-label="Delete album"
                    />
                  </Td>
                )}
              </Tr>
            ))}
          </Tbody>
        </Table>
        {showPagination && (
          <Flex justify="center" mt={4} align="center">
            <Button
              onClick={() => setCurrentPage(1)}
              isDisabled={currentPage === 1}
              mr={1}
              size="sm"
              colorScheme="gray"
              variant="outline"
            >
              처음
            </Button>
            <Button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              isDisabled={currentPage === 1}
              mr={2}
              size="sm"
              colorScheme="gray"
              variant="outline"
            >
              이전
            </Button>
            <HStack spacing={1} mx={4}>
              <Input
                value={tempPage}
                onChange={handlePageInput}
                size="sm"
                width="50px"
                textAlign="center"
                fontSize="sm"
              />
              <Button
                size="sm"
                onClick={handlePageMove}
                colorScheme="gray"
                variant="outline"
                minW="40px"
                px={2}
                color="gray.600"
                borderColor="gray.300"
                _hover={{
                  bg: "gray.100",
                  borderColor: "gray.400",
                }}
              >
                이동
              </Button>
              <Text>
                / {totalPages} 페이지 (전체 {totalOwnedAlbums}개)
              </Text>
            </HStack>
            <Button
              onClick={() => setCurrentPage((p) => p + 1)}
              isDisabled={currentPage >= totalPages}
              ml={2}
              size="sm"
              colorScheme="gray"
              variant="outline"
            >
              다음
            </Button>
            <Button
              onClick={() => setCurrentPage(totalPages)}
              isDisabled={currentPage >= totalPages}
              ml={1}
              size="sm"
              colorScheme="gray"
              variant="outline"
            >
              마지막
            </Button>
          </Flex>
        )}
      </Box>
    );
  };

  return (
    <>
      <Flex mb={4} justify="space-between" align="center">
        <Flex>
          <Input
            placeholder="검색"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={handleKeyPress}
            maxW="300px"
          />
          <IconButton
            icon={<SearchIcon />}
            ml={2}
            aria-label="Search"
            onClick={handleSearch}
          />
        </Flex>
        <Button
          leftIcon={<AddIcon />}
          colorScheme="blue"
          onClick={() => setIsModalOpen(true)}
        >
          앨범 등록
        </Button>
      </Flex>

      {/* 작성 중인 앨범 테이블 */}
      {renderAlbumTable(draftAlbums, false, "현재 작성중인 앨범", true)}

      {/* 소유한 앨범 테이블 */}
      {renderAlbumTable(ownedAlbums, true, "소유한 앨범", false)}

      <AlbumModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => {
          fetchDraftAlbums(currentSearchTerm);
          fetchOwnedAlbums(currentSearchTerm);
        }}
      />
    </>
  );
};

export default AlbumTab;
